import { inject, Injectable } from '@angular/core'
import { LanguageService } from '@modules/lang-module/services/language.service'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { WebSettingsInterface } from '@modules/user-module/interfaces/web-settings.interface'
import { combineLatest, Observable } from 'rxjs'
import { TradingViewHelperService } from '@modules/trading-view-module/services/trading-view-helper.service'
import { LanguageEnum } from '@modules/lang-module/enums/language.enum'
import { TradingViewTimeframesEnum } from '@modules/trading-view-module/enums/trading-view-timeframes.enum'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { AssetClassEnum } from '@modules/positions-module/enums/asset-class.enum'
import { ThemeService } from '@modules/theme-module/services/theme.service'

@Injectable({
	providedIn: 'root',
})
export class WebSettingsService {
	protected readonly languageEnum: typeof LanguageEnum = LanguageEnum
	protected readonly tradingViewTimeframesEnum: typeof TradingViewTimeframesEnum = TradingViewTimeframesEnum
	protected readonly assetClassEnum: typeof AssetClassEnum = AssetClassEnum

	private readonly _languageService: LanguageService = inject<LanguageService>(LanguageService)
	private readonly _themeService: ThemeService = inject<ThemeService>(ThemeService)
	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)
	private readonly _tradingViewHelperService: TradingViewHelperService =
		inject<TradingViewHelperService>(TradingViewHelperService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	get defaultWebSettings(): WebSettingsInterface {
		return {
			lang: this.languageEnum.ENGLISH,
			dashboards: [],
			tradingView: {
				state: null,
				timeframe: this.tradingViewTimeframesEnum.oneDay,
				period: null,
			},
		}
	}

	get streamsForWebSettings$(): Observable<unknown> {
		const streams$: Observable<unknown>[] = [
			this._languageService.language$,
			this._themeService.theme$,
			this._widgetsService.dashboards$,
			this._tradingViewHelperService.tradingViewState$,
			this._tradingViewHelperService.tradingViewTimeframe$,
			this._tradingViewHelperService.tradingViewPeriod$,
			this._userHelperService.isSecret$,
		]
		return combineLatest(streams$)
	}

	get collectedWebSettings(): WebSettingsInterface | null {
		if (!this._userHelperService.userDetail$.getValue()) return null

		return {
			lang: this._languageService.language$.getValue(),
			theme: this._themeService.theme$.getValue(),
			dashboards: this._widgetsService.formatDashboardsForWebSettings(),
			tradingView: {
				state: JSON.stringify(this._tradingViewHelperService.tradingViewState$.getValue()),
				timeframe: this._tradingViewHelperService.tradingViewTimeframe$.getValue(),
				period: this._tradingViewHelperService.tradingViewPeriod$.getValue(),
			},
			isSecret: this._userHelperService.isSecret$.getValue(),
		}
	}

	public distributeWebSettings(): void {
		const user: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()
		if (!user || !user.web_settings) return
		const { lang, theme, dashboards, tradingView, isSecret }: WebSettingsInterface = user.web_settings

		if (theme) this._themeService.toggleTheme(theme)
		if (lang) this._languageService.changeLanguage(lang)
		if (dashboards) this._widgetsService.applyDashboardsFromWebSettings(dashboards)
		if (tradingView?.state) this._tradingViewHelperService.setTradingViewState(JSON.parse(tradingView.state))
		if (tradingView?.timeframe) this._tradingViewHelperService.setTradingViewTimeframe(tradingView.timeframe)
		if (tradingView?.period) this._tradingViewHelperService.setTradingViewPeriod(tradingView.period)
		this._userHelperService.isSecret$.next(!!isSecret)
	}
}
